<template>
  <div class="flex flex-col gap-3">
    <div class="flex px-3 pt-10 justify-center gap-1 rounded-[10px] bg-warning">
      <div class="relative max-w-52 w-52">
        <img
          class="absolute left-0 bottom-0 w-full"
          src="../../assets/images/warning.png"
        />
      </div>
      <div class="text-white font-bold pl-2 pb-10">
        <h1 class="text-3xl">{{ $t("result.qr_not_st.wait") }}</h1>
        <p class="text-xl">{{ $t("result.qr_not_st.not_a_st_code") }}</p>
      </div>
    </div>
    <div class="p-3 flex flex-col gap-2.5 rounded bg-[#FAFAFA]">
      <h3 class="font-medium text-sm text-black">
        {{ $t("result.qr_not_st.can_not_authentificate") }}
      </h3>
      <div class="font-light text-xs text-black/80">
        <ul class="ltr:pl-4 rtl:pr-4">
          <li>{{ $t("result.qr_not_st.fake_text") }}</li>
          <li>{{ $t("result.qr_not_st.generic_code_text") }}</li>
        </ul>
        <p class="mt-5">
          {{ $t("result.qr_not_st.anothe_code_text") }}
        </p>
      </div>
    </div>
    <div class="flex flex-col items-center gap-1.5">
      <div class="text-[#F22A42] text-sm font-normal text-center">
        <h3>
          <span class="font-bold">{{
            $t("result.qr_not_st.it_is_a_counterfeit")
          }}</span
          ><br />
          {{ $t("result.qr_not_st.report_us") }}
        </h3>
      </div>
      <Button
        :isLoading="isLoading"
        buttonClass="bg-[#F22A42] text-white px-20 py-3"
        buttonName="result.qr_not_st.report"
        :onClick="handleReport"
      />
    </div>
    <div class="text-center">
      <Button
        buttonClass="underline text-base text-[#5D5D5D] font-normal"
        buttonName="result.qr_not_st.scan_another_code"
        :onClick="handleRescan"
      />
    </div>
  </div>
</template>

<script setup>
import { events } from "@/utils/events.js";
import authenticationService from "@/utils/authentication";
import { getAppName } from "@/utils/getAppName.js";
import { deviceInfo } from "@/utils/deviceInfo.js";
import getCountryName from "@/utils/getCountryName.js";
import { createPopup } from "@typeform/embed";
import "@typeform/embed/build/css/popup.css";
import * as amplitude from "@amplitude/analytics-browser";
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import Button from "../Button.vue";

const { locale } = useI18n();
const emits = defineEmits(["reopenCamera"]);
const props = defineProps({
  decodedData: Object,
});

const location = ref({});
const toggleReport = ref({});
const currentLanguage = ref(locale.value);
const isLoading = ref(false);
const qrUrl = ref("");
const unsuportedScanData = ref(null);
const device = ref(null);

const decodedData = props.decodedData
const app_name = getAppName()

onMounted(async () => {
  isLoading.value = true;

  device.value = await deviceInfo();

  if (events.value.carrier_content) {
    qrUrl.value = events.value.carrier_content;
  }

  try {
    location.value = events.value.locationData

    location.value.country = await getCountryName(
      location.value.latitude,
      location.value.longitude,
    );

    if (!unsuportedScanData.value) {
      const data = {
        latitude: location.value.latitude,
        longitude: location.value.longitude,
        model: decodedData.isDataFromMobileApp ? decodedData.device : device.value.model,
        os: decodedData.isDataFromMobileApp ? decodedData.os : device.value.platform,
        appName: app_name,
      }
      unsuportedScanData.value = await authenticationService.unsuportedScan(data);
    }

    isLoading.value = false;
    // eslint-disable-next-line no-unused-vars
  } catch (error) {
    isLoading.value = false;
  }

  await loadTypeform();
});

watch(
  () => locale.value,
  (newLang) => {
    currentLanguage.value = newLang;
  },
);

const loadTypeform = async () => {
  let formId = import.meta.env.VITE_FAKE_URL_REPORT_FORM_ID

  const options = {
    hideHeaders: true,
    hideFooter: true,
    autoClose: 2000,
    fullScreen: true,
    hidden: {
      country: location.value.country,
      lang: currentLanguage.value,
      latitude: location.value.latitude,
      longitude: location.value.longitude,
      url: qrUrl.value || '',
      source: location.value.source,
      userid: decodedData.isDataFromMobileApp ? decodedData.userid : '',
      scanid: unsuportedScanData.value.uuid,
      device: device.value.model,
      app_name: app_name,
      // eslint-disable-next-line no-undef
      app_version: __APP_VERSION__,
    },
    redirectTarget: "_parent",
  };
  toggleReport.value = createPopup(formId, {
    ...options,
  });
};

const handleReport = () => {
  amplitude.track("Report code continue", { ...decodedData });
  if (toggleReport.value.toggle()) toggleReport.value.toggle();
};

const handleRescan = () => {
  emits("reopenCamera");
};
</script>
